import React, {useEffect, useState, useRef, forwardRef, Ref} from 'react';
import {Link, useNavigate, useSearchParams} from 'react-router-dom';
import _ from 'lodash';
import classnames from 'classnames/bind';
import {
  IComponentVolumes,
  IOilFilter,
  IProd,
  IRecommendations,
  IState,
  IVehicle,
  ProductCategory,
  Replacement,
} from '../../../interface';
import style from './index.module.scss';
import Information from './Information';
import Loading from '../../Loading';
import {connect} from 'react-redux';
import {getProduct} from '../../../services';
import {useReactToPrint} from 'react-to-print';
import localesReplacement from '../../../utils/localesReplacement';
import findProductionsById from '../../../utils/findProductionsById';
import OwnerSelector from '../OwnerSelector';
import {ANALYTICS_BUTTON_NAME, ANALYTICS_EVENT, ANALYTICS_PAGE_TYPE, ANALYTICS_SEARCH_TYPE, APPNAME, ENABLEDWORKSHOPSOWNER, FASTSCAN} from '../../../constants';
import { Variant } from '../../../constants/variants';
import VehicleDropdown from '../VehicleDropdown';
import {useMediaQuery} from 'react-responsive';
import FindTheRightOil from '../FindTheRightOil';
import {useAppName} from '../../../hooks/use-app-names';
import UnderMaintenance from '../../UnderMaintenance';
import RecommendedProductPrintable from '../../RecommendationPrintable/Product';
import { useAnalytics } from '../../../hooks/use-analytics';

const cx = classnames.bind(style);
const primaryButton = 'primary-button';

interface IProps extends IOilFilter {
  replacements: Replacement;
  productCategoriesList: ProductCategory[] | null;
  vehicle: IVehicle | undefined;
  productHandler: (v: IProd) => void;
  recommendedProducts: IRecommendations[];
  trade: boolean;
  setTrade: (v: boolean) => void;
  vehicleComponentVolumes: IComponentVolumes[];
}

export const ProductInformation = (props: IProps) => {
  const {
    replacements,
    productCategoriesList,
    vehicle,
    productHandler,
    recommendedProducts,
    trade,
    setTrade,
    vehicleComponentVolumes,
  } = props;
  const navigation = useNavigate();
  const country = (localStorage.getItem('countryCode') ||
    process.env.REACT_APP_COUNTRYCODE) as string;
  const [searchParams, setSearchParams] = useSearchParams();
  const productId = searchParams.get('id');
  const [product, setProduct] = useState<IProd>();
  const [isMount, setIsMount] = useState<boolean>(false);
  const [underMaintnance, setUnderMaintenance] = useState<boolean>(false);
  const sendAnalytics = useAnalytics();
  const variant =  searchParams.get('variant');
  const isFord = variant === Variant.FORD;

  useEffect(() => {
    !productId && navigation('/');
    setIsMount(true);
    setUnderMaintenance(false);
    const controller = new AbortController();
    let isMounted = true;
    if (_.isNumber(_.toNumber(productId))) {
      const p = findProductionsById(recommendedProducts, _.toNumber(productId));
      if (p) {
        setProduct(p);
        productHandler(p as IProd);
        setIsMount(false);
      } else {
        getProduct(_.toNumber(productId), trade).then((d) => {
          if (isMounted) {
            if (!d) {
              setUnderMaintenance(true);
              setIsMount(false);
              return;
            }
            setProduct(d?.data);
            productHandler(d?.data);
            setIsMount(false);
          }
        });
      }
    }
    return () => {
      controller.abort();
      isMounted = false;
    };
  }, [productId, recommendedProducts, trade]);

  const ref = useRef<HTMLDivElement>(null);
  const printOptions = {
    content: () => ref?.current,
    copyStyles: true,
  };
  const handlePrint = useReactToPrint(printOptions);

  const handleShare = () => {
    const data = {
      title: `${vehicle?.make} - ${vehicle?.model} - ${vehicle?.type}`,
      text: localesReplacement(
        replacements,
        "Thank you for visiting Castrol Motor Oil and Fluid finder! Here's a link to the page you've shared",
      ),
      url: window.location.href,
    };
    if ('share' in navigator) {
      void (async function main() {
        try {
          await navigator.share(data);
        } catch (e) {
          const error = 1;
          throw new Error(error.toString());
        }
      })();
    } else {
      const body = encodeURIComponent(`${data.text} ${data.url}`);
      const url = `mailto:?subject=${encodeURIComponent(
        data.title,
      )}&body=${body}`;
      window.location.href = url;
    }
  };

  const isFastScan = useAppName(FASTSCAN);
  const i18n = localStorage.getItem('languageCode') as string;
  const [searchVisible, setSearchVisible] = useState(false);
  const isDesktopOrLaptop = useMediaQuery({minWidth: 1024});
  const onWTBClick = () => {
    sendAnalytics({
      event: ANALYTICS_EVENT.WHERE_TO_BUY_CLICK ,
      page: ANALYTICS_PAGE_TYPE.PRODUCT_FINDER,
      button: ANALYTICS_BUTTON_NAME.WHERE_TO_BUY,
      country: localStorage.getItem('countryCode') || '',
    });
    searchParams.set('page', 'wheretobuy');
    isFastScan && searchParams.set(APPNAME, FASTSCAN);
    setSearchParams(searchParams);
  };
  const onVehicleSelect = (value: string | number, model?: string) => {
    //value=18f847ba17864033e0659fbab47aa869
    sendAnalytics({
      event: ANALYTICS_EVENT.VIEW_RECOMMENDED_OILS_CLICK ,
      page: ANALYTICS_PAGE_TYPE.PRODUCT_FINDER,
      button: ANALYTICS_BUTTON_NAME.VIEW_RECOMMENDED_OILS,
      model: model,
      country: localStorage.getItem('countryCode') || '',
    });

    searchParams.set('page', 'recommendedProducts');
    searchParams.set('id', _.toString(value));
    isFastScan && searchParams.set(APPNAME, FASTSCAN);
    setSearchParams(searchParams);
    setSearchVisible(false);
  };
  const onProductSelect = (value: string | number, product?: string) => {
    sendAnalytics({
      event: ANALYTICS_EVENT.VIEW_PRODUCT_PAGE_CLICK ,
      page: ANALYTICS_PAGE_TYPE.PRODUCT_FINDER,
      search: ANALYTICS_SEARCH_TYPE.OIL_SEARCH,
      button: ANALYTICS_BUTTON_NAME.VIEW_RECOMMENDED_OILS,
      oilName: product,
      country: localStorage.getItem('countryCode') || '',
    });

    searchParams.set('page', 'product');
    searchParams.set('id', _.toString(value));
    isFastScan && searchParams.set(APPNAME, FASTSCAN);
    setSearchParams(searchParams);
    setSearchVisible(false);
  };

  const RenderPrint = forwardRef(function RenderPrint(
    props: {
      product: IProd | undefined;
      vehicle: IVehicle | undefined;
      volumns: IComponentVolumes[];
    },
    ref: Ref<any>,
  ) {
    return product ? (
      <RecommendedProductPrintable
        data={product}
        vehicle={vehicle}
        ref={ref}
        componentName=''
        volumes={vehicleComponentVolumes}
      />
    ) : null;
  });

  const goBack = (event: any) => {
    event.preventDefault();
    history.back();    
  };

  return (
    <>
      <RenderPrint
        product={product}
        vehicle={vehicle}
        ref={ref}
        volumns={vehicleComponentVolumes}
      />
      <>
        {/* Main container */}
        {isMount ? (
          <Loading />
        ) : (
          <div className={cx('content', {underMaintnance: underMaintnance})}>
            {/* filter section */}
            {!underMaintnance && (
              <div className={cx('bar')}>
                <div className={cx('link-back-content')}>
                  <Link to='#' onClick={goBack}>
                    {localesReplacement(replacements, 'Back')}
                  </Link>
                </div>
                <div className={cx('buttons')}>
                  <button
                    onClick={handlePrint}
                    className={`${cx(primaryButton)} ${cx('print')}`}
                  >
                    {localesReplacement(replacements, 'Print')}
                  </button>
                  <button
                    onClick={handleShare}
                    className={`${cx(primaryButton)} ${cx('share')}`}
                  >
                    {localesReplacement(replacements, 'Share')}
                  </button>
                </div>
              </div>
            )}
            <div className={cx('information')}>
              <div className='owner-selector'>
                {country && _.includes(ENABLEDWORKSHOPSOWNER, country) && !isFord ? (
                  <OwnerSelector
                    replacements={replacements}
                    selectedIndex={trade}
                    setSelectedIndex={(b) => {
                      sendAnalytics({
                        event: b ? ANALYTICS_EVENT.WORKSHOP_OWNER_CLICKS : ANALYTICS_EVENT.VEHICLE_OWNER_CLICKS ,
                        country: country,
                      });
                      setTrade(b);
                      sessionStorage.setItem('selectedType', _.toString(b));
                    }}
                  />
                ) : null}
              </div>
              {/* filter section */}
              {vehicle && (
                <div className={cx('filter-secion')}>
                  <div
                    className={cx('vehicle')}
                    onClick={() => setSearchVisible(true)}
                  >
                    <VehicleDropdown
                      dataList={vehicle}
                      className='vehicle-dropdown'
                      replacements={replacements}
                    />
                  </div>
                </div>
              )}
              {!underMaintnance ? (
                <>
                  {product && (
                    <Information
                      replacements={replacements}
                      dataList={product}
                      productCategories={
                        productCategoriesList ? productCategoriesList : []
                      }
                    />
                  )}
                </>
              ) : (
                <UnderMaintenance replacements={replacements} />
              )}
            </div>
          </div>
        )}
        {searchVisible && !isDesktopOrLaptop && (
          <div className={cx('findTheOil')}>
            <div
              className={cx('mask')}
              onClick={() => setSearchVisible(false)}
            ></div>
            <div className={`as-modal ${cx('flyout')}`}>
              <FindTheRightOil
                country={country}
                onVehicleSelect={onVehicleSelect}
                onProductSelect={onProductSelect}
                onWTBClick={onWTBClick}
                i18n={i18n}
                locales={replacements}
                baseAPI={process.env.REACT_APP_PROVIDER_URL}
                restore={isFastScan}
                mode={trade}
                setMode={setTrade}
              />
            </div>
          </div>
        )}
      </>
    </>
  );
};

const mapStateToProps = (state: IState) => ({
  replacements: state.replacements,
  productCategoriesList: state.productCategoriesList,
  vehicle: state.filter.vehicle,
  recommendedProducts: state.recommendedProducts,
  vehicleComponentVolumes: state.vehicleComponentVolumes,
});

export default connect(mapStateToProps)(ProductInformation);
